import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import managementService from '../api/managementService';
import { 
  renderDeviceChart, 
  renderAssetChart, 
  renderCustomerChart,
  renderDeviceTotal,
  renderAssetTotal,
  renderCustomerTotal,
  TelemetryChart
} from '@myio/energy-chart-sdk';

export default function TelemetryChartsDemo() {
  // Entity selection state
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  
  // Chart configuration state
  const [chartType, setChartType] = useState<'timeseries' | 'total'>('timeseries');
  const [entityType, setEntityType] = useState<'device' | 'asset' | 'customer'>('device');
  const [readingType, setReadingType] = useState<'energy' | 'water'>('energy');
  const [granularity, setGranularity] = useState<'1h' | '1d'>('1d');
  const [deep, setDeep] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Chart container and instance
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<TelemetryChart | null>(null);
  
  // Fetch customers
  const { data: customersResponse } = useQuery({
    queryKey: ['customers'],
    queryFn: () => managementService.customer.getAll(),
  });
  const customers = customersResponse?.data || [];
  
  // Fetch assets for selected customer
  const { data: assetsResponse } = useQuery({
    queryKey: ['customer-assets', selectedCustomer],
    queryFn: () => managementService.asset.getAll({ customerId: selectedCustomer }),
    enabled: !!selectedCustomer,
  });
  const assets = assetsResponse?.data || [];
  
  // Fetch devices for selected asset
  const { data: devicesResponse } = useQuery({
    queryKey: ['asset-devices', selectedAsset],
    queryFn: () => managementService.device.getAll({ assetId: selectedAsset }),
    enabled: !!selectedAsset,
  });
  const devices = devicesResponse?.data || [];
  
  // Reset dependent selections when parent changes
  useEffect(() => {
    setSelectedAsset('');
    setSelectedDevice('');
  }, [selectedCustomer]);
  
  useEffect(() => {
    setSelectedDevice('');
  }, [selectedAsset]);
  
  // Render chart when configuration changes
  useEffect(() => {
    if (!chartContainerRef.current) return;
    
    // Destroy existing chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }
    
    // Clear container
    chartContainerRef.current.innerHTML = '';
    
    // Determine which entity to use
    let entityId = '';
    if (entityType === 'device' && selectedDevice) {
      entityId = selectedDevice;
    } else if (entityType === 'asset' && selectedAsset) {
      entityId = selectedAsset;
    } else if (entityType === 'customer' && selectedCustomer) {
      entityId = selectedCustomer;
    }
    
    if (!entityId) {
      chartContainerRef.current.innerHTML = '<div class="text-center text-gray-500 py-8">Please select an entity to display the chart</div>';
      return;
    }
    
    // Common configuration
    const baseConfig = {
      clientId: 'ADMIN_DASHBOARD_CLIENT',
      clientSecret: 'admin_dashboard_secret_2025',
      readingType,
      startDate,
      endDate,
      theme,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
      iframeBaseUrl: window.location.origin,
    } as const;
    
    try {
      // Render appropriate chart based on entity type and chart type
      if (chartType === 'timeseries') {
        if (entityType === 'device') {
          chartInstanceRef.current = renderDeviceChart(chartContainerRef.current, {
            ...baseConfig,
            deviceId: entityId,
            granularity,
          });
        } else if (entityType === 'asset') {
          chartInstanceRef.current = renderAssetChart(chartContainerRef.current, {
            ...baseConfig,
            assetId: entityId,
            granularity,
            deep,
          });
        } else if (entityType === 'customer') {
          chartInstanceRef.current = renderCustomerChart(chartContainerRef.current, {
            ...baseConfig,
            customerId: entityId,
            granularity,
            deep,
          });
        }
      } else { // total
        if (entityType === 'device') {
          chartInstanceRef.current = renderDeviceTotal(chartContainerRef.current, {
            ...baseConfig,
            deviceId: entityId,
          });
        } else if (entityType === 'asset') {
          chartInstanceRef.current = renderAssetTotal(chartContainerRef.current, {
            ...baseConfig,
            assetId: entityId,
            deep,
          });
        } else if (entityType === 'customer') {
          chartInstanceRef.current = renderCustomerTotal(chartContainerRef.current, {
            ...baseConfig,
            customerId: entityId,
            deep,
          });
        }
      }
      
      // Listen for drilldown events
      if (chartInstanceRef.current && chartType === 'timeseries') {
        chartInstanceRef.current.on('drilldown', (data) => {
          setStartDate(data.startDate);
          setEndDate(data.endDate);
          setGranularity(data.interval === '1 hour' ? '1h' : '1d');
        });
      }
    } catch (error) {
      console.error('Failed to render chart:', error);
      chartContainerRef.current.innerHTML = '<div class="text-center text-red-500 py-8">Failed to render chart</div>';
    }
  }, [
    entityType, 
    chartType, 
    selectedCustomer, 
    selectedAsset, 
    selectedDevice,
    readingType,
    startDate,
    endDate,
    granularity,
    deep,
    theme
  ]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);
  
  const setQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    // Set appropriate granularity based on range
    if (days <= 7) {
      setGranularity('1h');
    } else {
      setGranularity('1d');
    }
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Telemetry Charts Demo</h1>
      
      {/* Description */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-blue-900 mb-3">About This Demo</h2>
        <p className="text-blue-800 mb-4">
          This demo showcases the comprehensive telemetry charting SDK that supports all API endpoint types:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
          <div>
            <h3 className="font-semibold mb-2">Time Series Charts:</h3>
            <ul className="space-y-1">
              <li>• Device consumption over time</li>
              <li>• Asset aggregated consumption</li>
              <li>• Customer total consumption</li>
              <li>• Drilldown from daily to hourly</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Total Charts:</h3>
            <ul className="space-y-1">
              <li>• Device total consumption</li>
              <li>• Asset aggregated totals</li>
              <li>• Customer total consumption</li>
              <li>• Device count indicators</li>
            </ul>
          </div>
        </div>
        <p className="text-blue-800 mt-4">
          <strong>Features:</strong> Energy/Water support, Light/Dark themes, Deep aggregation, Responsive design
        </p>
      </div>
      
      {/* Configuration Panel */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Chart Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Entity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity Type
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as 'device' | 'asset' | 'customer')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="device">Device</option>
              <option value="asset">Asset</option>
              <option value="customer">Customer</option>
            </select>
          </div>
          
          {/* Chart Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chart Type
            </label>
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value as 'timeseries' | 'total')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="timeseries">Time Series</option>
              <option value="total">Total</option>
            </select>
          </div>
          
          {/* Reading Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reading Type
            </label>
            <select
              value={readingType}
              onChange={(e) => setReadingType(e.target.value as 'energy' | 'water')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="energy">Energy (kWh)</option>
              <option value="water">Water (m³)</option>
            </select>
          </div>
          
          {/* Granularity Selection (only for time series) */}
          {chartType === 'timeseries' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Granularity
              </label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value as '1h' | '1d')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="1h">Hourly</option>
                <option value="1d">Daily</option>
              </select>
            </div>
          )}
          
          {/* Deep Aggregation (only for asset/customer) */}
          {(entityType === 'asset' || entityType === 'customer') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deep Aggregation
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="deep"
                  checked={deep}
                  onChange={(e) => setDeep(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="deep" className="ml-2 text-sm text-gray-700">
                  Include sub-entities
                </label>
              </div>
            </div>
          )}
          
          {/* Theme Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theme
            </label>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as 'light' | 'dark')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
        </div>
        
        {/* Entity Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Customer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              disabled={entityType === 'device'}
            >
              <option value="">Select a customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Asset Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset
            </label>
            <select
              value={selectedAsset}
              onChange={(e) => setSelectedAsset(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              disabled={!selectedCustomer || entityType === 'customer'}
            >
              <option value="">Select an asset</option>
              {assets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Device Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device
            </label>
            <select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              disabled={!selectedAsset || entityType !== 'device'}
            >
              <option value="">Select a device</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.name || `${device.gatewayId} - Slave ${device.slaveId}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Date Range Selection */}
        <div className="border-t pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setQuickRange(7)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                7 days
              </button>
              <button
                onClick={() => setQuickRange(30)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                30 days
              </button>
              <button
                onClick={() => setQuickRange(90)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                90 days
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart Display */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Chart Preview</h2>
        
        <div 
          ref={chartContainerRef}
          className="w-full"
          style={{ height: '500px', minHeight: '400px' }}
        >
          {/* Chart will be rendered here */}
        </div>
      </div>
      
      {/* Configuration Summary */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Current Configuration</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <p><strong>Entity:</strong> {entityType.charAt(0).toUpperCase() + entityType.slice(1)}</p>
          <p><strong>Chart Type:</strong> {chartType === 'timeseries' ? 'Time Series' : 'Total'}</p>
          <p><strong>Reading Type:</strong> {readingType === 'energy' ? 'Energy (kWh)' : 'Water (m³)'}</p>
          {chartType === 'timeseries' && (
            <p><strong>Granularity:</strong> {granularity === '1h' ? 'Hourly' : 'Daily'}</p>
          )}
          {(entityType === 'asset' || entityType === 'customer') && (
            <p><strong>Deep Aggregation:</strong> {deep ? 'Yes' : 'No'}</p>
          )}
          <p><strong>Period:</strong> {startDate} to {endDate}</p>
        </div>
      </div>

      {/* SDK Usage Examples */}
      <div className="mt-6 bg-gray-900 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">SDK Usage Example</h3>
        <div className="text-sm text-gray-300 font-mono space-y-4">
          <div>
            <p className="text-gray-400 mb-2">// Current configuration would be implemented as:</p>
            <pre className="text-green-400 whitespace-pre-wrap">
{`import { ${chartType === 'timeseries' 
  ? `render${entityType.charAt(0).toUpperCase() + entityType.slice(1)}Chart`
  : `render${entityType.charAt(0).toUpperCase() + entityType.slice(1)}Total`} } from './sdk/energyChartSDK';

const chart = ${chartType === 'timeseries' 
  ? `render${entityType.charAt(0).toUpperCase() + entityType.slice(1)}Chart`
  : `render${entityType.charAt(0).toUpperCase() + entityType.slice(1)}Total`}('#chart-container', {
  clientId: 'ADMIN_DASHBOARD_CLIENT',
  clientSecret: 'admin_dashboard_secret_2025',
  ${entityType}Id: '${entityType === 'device' ? selectedDevice : entityType === 'asset' ? selectedAsset : selectedCustomer}',
  readingType: '${readingType}',
  startDate: '${startDate}',
  endDate: '${endDate}',${chartType === 'timeseries' ? `
  granularity: '${granularity}',` : ''}${(entityType === 'asset' || entityType === 'customer') ? `
  deep: ${deep},` : ''}
  theme: '${theme}',
  apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000'
});

// Listen for drilldown events (time-series only)
${chartType === 'timeseries' ? `chart.on('drilldown', (data) => {
  console.log('Drilled down to:', data);
});` : '// No drilldown events for total charts'}

// Clean up when done
// chart.destroy();`}
            </pre>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-400">
          <p><strong>Available Functions:</strong></p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><code className="text-yellow-400">renderDeviceChart()</code> - Device time-series</li>
            <li><code className="text-yellow-400">renderAssetChart()</code> - Asset time-series</li>
            <li><code className="text-yellow-400">renderCustomerChart()</code> - Customer time-series</li>
            <li><code className="text-yellow-400">renderDeviceTotal()</code> - Device total</li>
            <li><code className="text-yellow-400">renderAssetTotal()</code> - Asset total</li>
            <li><code className="text-yellow-400">renderCustomerTotal()</code> - Customer total</li>
            <li><code className="text-yellow-400">renderTelemetryChart()</code> - Universal function</li>
          </ul>
        </div>
      </div>
    </div>
  );
}